import * as React from "react";
import { Link } from "react-router";

import { useAllOrgSecrets } from "@/shared/domain/secrets/hooks/useAllOrgSecrets";
import {
  Radio,
  RadioGroup,
  RobotoButton,
} from "@/shared/foundation/primitives";
import { Goto } from "@/shared/navigation/Goto";

import styles from "./OrgSecretPicker.module.css";

interface OrgSecretPickerProps {
  orgId: string;
  legend: string;
  /** What the secret holds, shown before the "Manage secrets" link. */
  description: string;
  /** Link text offered when the org has no secrets, e.g. "Add the token as a secret". */
  addSecretLabel: string;
  value: string;
  onValueChange: (secretName: string) => void;
  disabled?: boolean;
  /** Label for an extra first choice of no secret, whose value is "". */
  noneLabel?: string;
}

/**
 * Picks one of the org's secrets, for a form inside a dialog. Its links open
 * Settings › Secrets in a new tab, and the list refetches whenever this tab
 * regains focus, so a secret added there can be picked here.
 */
export function OrgSecretPicker({
  orgId,
  legend,
  description,
  addSecretLabel,
  value,
  onValueChange,
  disabled,
  noneLabel,
}: OrgSecretPickerProps) {
  // "always": the app's staleTime would otherwise skip the focus refetch.
  const secretsQuery = useAllOrgSecrets(orgId, {
    refetchOnWindowFocus: "always",
  });
  const secretNames = secretsQuery.data?.map((secret) => secret.name) ?? [];

  return (
    <>
      {/* A radio list rather than a Select: the Select popup (z-index 1300)
          renders under the Dialog (1400). */}
      <RadioGroup<string>
        legend={legend}
        description={
          <>
            {description} <SecretsLink>Manage secrets</SecretsLink>
          </>
        }
        value={value}
        onValueChange={onValueChange}
        disabled={disabled}
      >
        {noneLabel !== undefined && (
          <Radio<string> value="" label={noneLabel} />
        )}
        {secretNames.map((name) => (
          <Radio<string> key={name} value={name} label={name} />
        ))}
      </RadioGroup>
      {secretsQuery.isError && (
        <div className={styles.loadError} role="alert">
          <p className={styles.error}>
            Couldn&apos;t load the org&apos;s secrets.
          </p>
          <RobotoButton
            size="sm"
            variant="secondary"
            onClick={() => void secretsQuery.refetch()}
            loading={secretsQuery.isFetching}
          >
            Retry
          </RobotoButton>
        </div>
      )}
      {secretsQuery.isSuccess && secretNames.length === 0 && (
        <p className={styles.note}>
          This organization has no secrets yet.{" "}
          <SecretsLink>{addSecretLabel}</SecretsLink>, then pick it here.
        </p>
      )}
    </>
  );
}

/** A link to Settings › Secrets in a new tab, so the open dialog survives. */
function SecretsLink({ children }: { children: React.ReactNode }) {
  return (
    <Link
      to={Goto.route.Settings.Secrets}
      target="_blank"
      rel="noopener"
      className={styles.hintLink}
    >
      {children}
    </Link>
  );
}
